import currency from "currency.js";
import { OrderTypeResponse } from "../../codegen-api";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { Align } from "../shared/Table/style";
import { renderFeeAmount, renderSignedAmount, titleFor } from "./helpers";
import {
  ColumnContext,
  PriceColumnOptions,
  SignedNumberColumnOptions,
  WithDecorations,
} from "./types";

export function buildPriceColumn<
  T extends WithDecorations<{ order_type?: string }>
>(
  { t, tooltips }: Pick<ColumnContext, "t" | "tooltips">,
  { accessor, titleKey = "price", marketOrderDashes }: PriceColumnOptions
): ITableColumn<T> {
  return {
    title: titleFor(t, titleKey),
    accessor: accessor as any,
    align: "right",
    tooltip: tooltips("avgEntryPrice"),
    Cell: ({ value, row }) => {
      const numeric = Number(value);
      if (!value || !Number.isFinite(numeric)) return <Align align="right">-</Align>;
      if (marketOrderDashes) {
        const isMarket =
          String(row.original.order_type).toLowerCase() === OrderTypeResponse.Market;
        if (isMarket) return <Align align="right">-</Align>;
      }
      return (
        <Align align="right">
          {currency(numeric, { precision: row.original.pricePrecision }).format()}
        </Align>
      );
    },
  };
}

export function buildSignedNumberColumn<T>(
  { t, tooltips }: Pick<ColumnContext, "t" | "tooltips">,
  { accessor, titleKey, tooltipKey, invert }: SignedNumberColumnOptions
): ITableColumn<T> {
  return {
    title: titleFor(t, titleKey),
    accessor: accessor as any,
    align: "right",
    tooltip: tooltipKey ? tooltips(tooltipKey) : undefined,
    Cell: ({ value }) => (
      <Align align="right">
        {invert ? renderFeeAmount(value as any) : renderSignedAmount(value as any)}
      </Align>
    ),
  };
}

export function buildOrderTypeColumn<T>({
  t,
  tooltips,
}: Pick<ColumnContext, "t" | "tooltips">): ITableColumn<T> {
  return {
    title: titleFor(t, "order_type"),
    accessor: "order_type" as any,
    tooltip: tooltips("orderType"),
    valueExtractor: (value) =>
      value
        ? String(value).toLowerCase() === OrderTypeResponse.Limit
          ? t("limit", { defaultValue: "Limit" })
          : t("market", { defaultValue: "Market" })
        : "-",
  };
}
